import type { OnboardingSection } from "@qendo/onboarded";
import type { Href } from "expo-router";
import { type ReactNode, useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import Animated, { useSharedValue, withTiming } from "react-native-reanimated";
import { Button } from "~/components/Button";
import { Columns } from "~/components/layout/Columns";
import { Subtitle, Title } from "~/components/layout/text";
import { Nothing } from "~/components/permission/Nothing";
import type { QendoIconSet } from "~/components/QendoIcon";
import { Flex, Gap } from "~/constants/layout";
import { Radius, Space } from "~/constants/sizing";
import { useOnboarding } from "~/providers/OnboardingProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { setHeightOnLayout } from "~/util/layout";
import { HomeIcon } from "./HomeIcon";

type OnboardingCardProps = {
    title: ReactNode;
    icon: QendoIconSet;
    subtitle: string;
    button: Href;
    buttonText: string;
    readonly section: OnboardingSection;
};

export const OnboardingCard = ({ title, subtitle, icon, button, buttonText, section }: OnboardingCardProps) => {
    const { background } = useTheme();
    const { visible, dismiss } = useOnboarding();
    const [height, setHeight] = useState(400);
    const handleOnLayout = setHeightOnLayout(setHeight);
    const show = visible(section);
    const [dismissing, setDismissing] = useState(false);
    const maxHeight = useSharedValue(height);

    const handleDismiss = () => {
        if (show && !dismissing) {
            setDismissing(true);
            maxHeight.value = withTiming(0);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(dismiss): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(section): readonly
    useEffect(() => {
        if (dismissing && height === 0) {
            dismiss(section);
        } else if (!dismissing && height > 0 && maxHeight.value !== height) {
            maxHeight.value = height;
        }
    }, [dismissing, height, maxHeight]);

    if (!show) {
        return <Nothing />;
    }

    return (
        <Animated.View style={[styles.animated, dismissing && { maxHeight }]} onLayout={handleOnLayout}>
            <View style={[styles.container, background.primary]}>
                <View style={[Gap.lg, Flex.start]}>
                    <HomeIcon icon={icon} />
                    <View style={Gap.sm}>
                        <Title>{title}</Title>
                        <Subtitle size="base" colour="primary">
                            {subtitle}
                        </Subtitle>
                    </View>
                </View>
                <Columns
                    items={[
                        () => <Button size="lg" href={button} variant="primary" title={buttonText} />,
                        () => <Button size="lg" variant="secondary" title="Dismiss" onPress={handleDismiss} />,
                    ]}
                />
            </View>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    animated: {
        overflow: "hidden",
    },
    container: {
        gap: Space.lg,
        borderRadius: Radius.lg,
        padding: Space.lg,
        alignItems: "stretch",
    },
});
